// web/src/pages/Home.tsx
// frame.sandstonecloud.com — Full web scoring app
// Cross-platform: iOS Safari, Chrome Android, desktop browsers
// Layout: Mode → Weights → Chat Box → CTA (no overlap, clear separation)
import React, { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { scorePhotosWeb, type WebScoringOptions } from '../scoring/webScorer';
import {
  SCORING_MODES, DIMENSIONS, TIER_ENTITLEMENTS, SubscriptionTier,
  type ScoringModeId, type PhotoScore, type WeightProfile,
} from '@frame/scoring-types';
import { formatScore, percentileLabel } from '@frame/utils';
import { WebAuthService, type AuthState } from '../services/auth';
import { UsageTracker } from '../services/usageTracker';

// ─── Admin override ────────────────────────────────────────
const ADMIN_USERNAME = 'sandstone-admin';
const ADMIN_PASSWORD = 'Iamgeekn69!';

// ─── Design tokens (Sandstone Cloud dark navy) ─────────────
const C = {
  bg: '#0f172a', text: '#f8fafc', gold: '#d4a574', goldLight: 'rgba(212,165,116,0.12)',
  goldMedium: 'rgba(212,165,116,0.22)', sage: '#22c55e', surface: '#1e293b',
  secondary: '#94a3b8', divider: '#334155', error: 'rgba(239, 68, 68, 0.7)',
  white: '#FFFFFF', blue: '#2563eb', blueLight: '#3b82f6',
  surfaceHover: '#334155', muted: '#64748b',
} as const;

/** Score-color coding: red (<4), orange (4-5), amber (5-6), green (6-8), teal (8+) */
function scoreColor(score: number): string {
  if (score < 4) return '#ef4444';
  if (score < 5) return '#f97316';
  if (score < 6) return '#f59e0b';
  if (score < 8) return '#22c55e';
  return '#10b981';
}

const MODE_ORDER: ScoringModeId[] = [
  'balanced', 'dating', 'instagram', 'professional', 'travel', 'realEstate', 'custom',
];

// ─── Responsive helpers ─────────────────────────────────────
// CSS custom properties injected once for viewport units
const injectCSS = () => {
  if (typeof document === 'undefined') return;
  if (document.getElementById('frame-web-css')) return;
  const style = document.createElement('style');
  style.id = 'frame-web-css';
  style.textContent = `
    /* Fix iOS 100vh bug — use dvh where supported */
    :root {
      --vh: 1vh;
      --safe-top: env(safe-area-inset-top, 0px);
      --safe-bottom: env(safe-area-inset-bottom, 0px);
      --safe-left: env(safe-area-inset-left, 0px);
      --safe-right: env(safe-area-inset-right, 0px);
    }
    @supports (height: 1dvh) {
      :root { --vh: 1dvh; }
    }
    /* Prevent rubber-banding scroll on iOS */
    html, body { overscroll-behavior: none; }
    /* Smooth momentum scrolling on iOS */
    .frame-scroll { -webkit-overflow-scrolling: touch; }
    /* Prevent text selection on interactive elements */
    .frame-chip { -webkit-user-select: none; user-select: none; }
    /* Fix Android Chrome address bar resize flicker */
    @supports (height: 100dvh) {
      .frame-page { min-height: 100dvh; }
    }
    /* Focus ring for accessibility */
    .frame-focus:focus-visible {
      outline: 2px solid ${C.blue};
      outline-offset: 2px;
      border-radius: 8px;
    }
    /* Textarea iOS fix — prevent zoom, proper resize */
    textarea.frame-vibe {
      font-size: 16px !important; /* iOS zoom prevention */
      -webkit-appearance: none;
      appearance: none;
    }
    /* Scrollbar styling */
    .frame-scroll::-webkit-scrollbar { width: 4px; height: 4px; }
    .frame-scroll::-webkit-scrollbar-track { background: transparent; }
    .frame-scroll::-webkit-scrollbar-thumb { background: ${C.surfaceHover}; border-radius: 2px; }
    /* Animations */
    @keyframes frameSlideUp {
      from { opacity: 0; transform: translateY(12px); }
      to { opacity: 1; transform: translateY(0); }
    }
    @keyframes frameFadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }
    @keyframes frameBarGrow {
      from { width: 0%; }
    }
    .frame-animate-in {
      animation: frameSlideUp 0.4s ease-out forwards;
    }
    .frame-fade-in {
      animation: frameFadeIn 0.3s ease-out forwards;
    }
    .frame-bar-animate {
      animation: frameBarGrow 0.6s ease-out forwards;
    }
    /* Mode chip hover */
    .frame-chip:hover { transform: scale(0.97); }
    .frame-chip:active { transform: scale(0.95); }
    /* CTA hover */
    .frame-cta:hover:not(:disabled) { transform: scale(1.01); box-shadow: 0 4px 20px rgba(37,99,235,0.4); }
    .frame-cta:active:not(:disabled) { transform: scale(0.99); }
    /* Photo card hover */
    .frame-photo:hover { transform: scale(1.03); z-index: 2; }
    /* Modal backdrop animation */
    .frame-modal-backdrop { animation: frameFadeIn 0.2s ease-out; }
  `;
  document.head.appendChild(style);
};

// ─── Main App ───────────────────────────────────────────────
export function Home() {
  const [files, setFiles] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const [selectedMode, setSelectedMode] = useState<ScoringModeId>('balanced');
  const [vibeText, setVibeText] = useState('');
  const [requestedCount, setRequestedCount] = useState(25);
  const [scoring, setScoring] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [results, setResults] = useState<PhotoScore[]>([]);
  const [selectedPhoto, setSelectedPhoto] = useState<PhotoScore | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);
  const vibeRef = useRef<HTMLTextAreaElement>(null);

  // Auth + usage state
  const [authState, setAuthState] = useState<AuthState>({ isAuthenticated: false, user: null, isLoading: true });
  const [showAuthGate, setShowAuthGate] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [authMode, setAuthMode] = useState<'login' | 'signup' | 'verify' | 'forgot'>('signup');
  const [authEmail, setAuthEmail] = useState('');
  const [authPassword, setAuthPassword] = useState('');
  const [authCode, setAuthCode] = useState('');
  const [authError, setAuthError] = useState('');
  const [authLoading, setAuthLoading] = useState(false);

  // Subscription tier + admin
  const [userTier, setUserTier] = useState<SubscriptionTier>(SubscriptionTier.Free);
  const [isAdmin, setIsAdmin] = useState(false);
  const [sessionPhotosScored, setSessionPhotosScored] = useState<Record<string, number>>({});

  // Tier entitlements for current user
  const entitlements = TIER_ENTITLEMENTS[userTier];

  // Check auth on mount
  useEffect(() => {
    if (!WebAuthService.isConfigured()) {
      setAuthState({ isAuthenticated: false, user: null, isLoading: false });
      return;
    }
    WebAuthService.getCurrentUser().then(user => {
      if (user) {
        setAuthState({ isAuthenticated: true, user: { userId: user.userId, email: user.signInDetails?.loginId ?? '' }, isLoading: false });
      } else {
        setAuthState({ isAuthenticated: false, user: null, isLoading: false });
      }
    }).catch(() => {
      setAuthState({ isAuthenticated: false, user: null, isLoading: false });
    });
  }, []);

  // Inject CSS once on mount
  useEffect(() => { injectCSS(); }, []);

  // Cleanup preview URLs
  useEffect(() => {
    return () => previews.forEach(URL.revokeObjectURL);
  }, [previews]);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (selectedPhoto) {
      document.body.style.overflow = 'hidden';
      // iOS Safari scroll lock
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
    } else {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    };
  }, [selectedPhoto]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files ?? []);
    if (selected.length === 0) return;
    setFiles(selected);
    const urls = selected.map((f) => URL.createObjectURL(f));
    setPreviews(urls);
    setResults([]);
    setSelectedPhoto(null);
  }, []);

  const handleScore = useCallback(async () => {
    if (files.length === 0) return;

    // Auth gate: require account after 25 free photos
    if (!authState.isAuthenticated && UsageTracker.requiresAuth()) {
      setShowAuthGate(true);
      return;
    }

    // Premium mode gate
    const mode = SCORING_MODES[selectedMode];
    if (mode.requiresPremium && userTier === SubscriptionTier.Free) {
      setShowUpgrade(true);
      return;
    }

    // Per-session photo limit gate
    const sessionLimit = entitlements.photosPerSession[selectedMode];
    const used = sessionPhotosScored[selectedMode] || 0;
    if (sessionLimit !== Infinity && used + files.length > sessionLimit && userTier === SubscriptionTier.Free) {
      setShowUpgrade(true);
      return;
    }

    setScoring(true);
    setProgress({ current: 0, total: files.length });

    try {
      const weights: WeightProfile = mode.defaultWeights;
      const options: WebScoringOptions = {
        weights,
        onProgress: (current, total) => setProgress({ current, total }),
      };

      const scores = await scorePhotosWeb(files, options);
      setResults(scores);

      // Track per-mode session usage
      setSessionPhotosScored(prev => ({
        ...prev,
        [selectedMode]: (prev[selectedMode] || 0) + files.length,
      }));

      // Track usage for free-tier gating
      if (!authState.isAuthenticated) {
        UsageTracker.recordSession(files.length);
      }

      setTimeout(() => {
        resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 200);
    } catch (err) {
      console.error('[FRAME Web] Scoring error:', err);
    } finally {
      setScoring(false);
    }
  }, [files, selectedMode, authState.isAuthenticated, userTier, entitlements, sessionPhotosScored]);

  const selectedCount = useMemo(
    () => Math.min(requestedCount, results.length),
    [requestedCount, results.length],
  );

  const allComposites = useMemo(
    () => results.map((r) => r.compositeScore),
    [results],
  );

  const currentMode = SCORING_MODES[selectedMode];
  const modeWeights = [
    currentMode.defaultWeights.portraitWeight,
    currentMode.defaultWeights.lightingWeight,
    currentMode.defaultWeights.compositionWeight,
    currentMode.defaultWeights.sceneWeight,
  ];

  return (
    <div className="frame-page" style={S.page}>
      {/* ─── Header ─── */}
      <header style={S.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <img src="/logo.png" alt="FRAME" style={{ height: 36, width: 36, borderRadius: 8 }} />
          <div>
            <h1 style={S.brand}>FRAME</h1>
            <p style={S.brandSub}>AI Photo Curation</p>
          </div>
        </div>
        <nav style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          {authState.isAuthenticated ? (
            <>
              {isAdmin && <span style={{ fontSize: 11, color: '#10b981', fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase' as const }}>ADMIN</span>}
              {userTier !== SubscriptionTier.Free && (
                <span style={{ fontSize: 11, color: userTier === SubscriptionTier.Gold ? C.gold : '#94a3b8', fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase' as const }}>
                  {userTier === SubscriptionTier.Gold ? '★ GOLD' : 'SILVER'}
                </span>
              )}
              <span style={{ fontSize: 13, color: C.secondary }}>
                {authState.user?.email?.split('@')[0]}
              </span>
              {userTier === SubscriptionTier.Free && (
                <button onClick={() => setShowUpgrade(true)} style={S.headerLink}>
                  Upgrade
                </button>
              )}
              <button onClick={async () => {
                if (!isAdmin) await WebAuthService.signOut();
                setAuthState({ isAuthenticated: false, user: null, isLoading: false });
                setUserTier(SubscriptionTier.Free);
                setIsAdmin(false);
                setSessionPhotosScored({});
              }} style={S.headerLink}>
                Sign Out
              </button>
            </>
          ) : (
            <>
              <button onClick={() => { setAuthMode('login'); setShowAuthGate(true); }} style={S.headerLink}>
                Log In
              </button>
              <a href="/privacy" style={S.headerLink}>Privacy</a>
            </>
          )}
        </nav>
      </header>

      <main style={S.main} className="frame-scroll">
        {/* ─── Hero ─── */}
        <section style={S.hero} className="frame-animate-in">
          <h2 style={S.heroTitle}>
            Find your best photos<br />
            <span style={{ color: C.gold, background: 'linear-gradient(135deg, #d4a574, #e8c9a0)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>with transparent AI scoring</span>
          </h2>
          <p style={S.heroBody}>
            FRAME scores every photo across portrait composition, lighting, composition,
            and scene interest. All processing happens in your browser — nothing is uploaded.
          </p>
        </section>

        {/* ─── Upload Section ─── */}
        <section style={S.card} className="frame-animate-in" role="region" aria-label="Photo selection">
          <div
            onClick={() => fileRef.current?.click()}
            style={S.dropzone}
            role="button"
            tabIndex={0}
            className="frame-focus"
            onKeyDown={(e) => e.key === 'Enter' && fileRef.current?.click()}
          >
            {files.length > 0 ? (
              <div>
                <p style={S.dropzoneCount}>{files.length} photos selected</p>
                <p style={S.dropzoneHint}>Tap to change selection</p>
                <div style={S.thumbStrip}>
                  {previews.slice(0, 6).map((url, i) => (
                    <img key={i} src={url} alt="" style={S.thumbImg} loading="lazy" />
                  ))}
                  {files.length > 6 && (
                    <div style={S.thumbMore}>+{files.length - 6}</div>
                  )}
                </div>
              </div>
            ) : (
              <div>
                <div style={S.uploadIcon}>📷</div>
                <p style={S.dropzoneTitle}>Select photos to score</p>
                <p style={S.dropzoneHint}>
                  All analysis happens in your browser. Your photos never leave your device.
                </p>
              </div>
            )}
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/heic"
              multiple
              onChange={handleFileSelect}
              style={{ display: 'none' }}
              aria-label="Select photos"
            />
          </div>
        </section>

        {/* ════════════════════════════════════════════════════
            SECTION 1: SCORING MODE + WEIGHT SLIDERS
            This section is ABOVE the chat box — fixed order, no overlap
            ════════════════════════════════════════════════════ */}
        {files.length > 0 && (
          <section style={S.card} className="frame-animate-in" role="region" aria-label="Scoring mode">
            <h3 style={S.sectionLabel}>Scoring Mode</h3>

            {/* Mode chip row — horizontal scrollable on mobile */}
            <div style={S.modeChipScroll} className="frame-scroll">
              <div style={S.modeChipRow}>
                {MODE_ORDER.map((id) => {
                  const mode = SCORING_MODES[id];
                  const isActive = selectedMode === id;
                  const isLocked = mode.requiresPremium && userTier === SubscriptionTier.Free;
                  const sessionLimit = entitlements.photosPerSession[id];
                  const used = sessionPhotosScored[id] || 0;
                  const atLimit = sessionLimit !== Infinity && used >= sessionLimit && userTier === SubscriptionTier.Free;
                  return (
                    <button
                      key={id}
                      className="frame-chip frame-focus"
                      onClick={() => {
                        if (isLocked) { setShowUpgrade(true); return; }
                        setSelectedMode(id);
                      }}
                      style={{
                        ...S.modeChip,
                        backgroundColor: isActive ? C.gold : isLocked ? C.surfaceHover : C.surface,
                        color: isActive ? C.white : isLocked ? C.muted : C.text,
                        fontWeight: isActive ? 600 : 500,
                        opacity: isLocked ? 0.6 : 1,
                      }}
                      aria-pressed={isActive}
                    >
                      {mode.label}
                      {isLocked && <span style={S.lockBadge}>🔒</span>}
                      {atLimit && !isLocked && <span style={{ ...S.lockBadge, color: C.error }}>limit</span>}
                    </button>
                  );
                })}
              </div>
            </div>
            <p style={S.tagline}>{currentMode.tagline}</p>

            {/* Divider */}
            <div style={S.sectionDivider} />

            {/* Weight distribution bars — clearly separated */}
            <h3 style={{ ...S.sectionLabel, marginTop: 0 }}>Weight Distribution</h3>
            <div style={S.weightGrid}>
              {DIMENSIONS.map((dim, idx) => {
                const pct = Math.round(modeWeights[idx] * 100);
                return (
                  <div key={dim.key} style={S.weightRow}>
                    <span style={S.weightLabel}>{dim.naturalLanguageLabel}</span>
                    <div style={S.weightBarTrack}>
                      <div
                        className="frame-bar-animate"
                        style={{ ...S.weightBarFill, width: `${pct}%` }}
                      />
                    </div>
                    <span style={S.weightPct}>{pct}%</span>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* ════════════════════════════════════════════════════
            SECTION 2: VIBE CHAT BOX + PHOTO COUNT + CTA
            Clearly BELOW the mode/weights section — enlarged, no overlap
            ════════════════════════════════════════════════════ */}
        {files.length > 0 && (
          <section style={S.card} className="frame-animate-in" role="region" aria-label="Vibe description">
            <h3 style={S.sectionLabel}>Describe Your Vibe</h3>

            <textarea
              ref={vibeRef}
              className="frame-vibe frame-focus"
              value={vibeText}
              onChange={(e) => setVibeText(e.target.value)}
              placeholder={`Describe what you're looking for...\n\nExamples:\n• "Best photos from my Italy trip with good lighting"\n• "Professional headshots with clean backgrounds"\n• "Candid moments, skip group shots"\n• "Golden hour near water"\n• "Dating pics — natural smile, good light, no crowds"`}
              style={S.vibeInput}
              maxLength={500}
              rows={8}
              aria-label="Describe your photo preferences"
            />
            <p style={S.chatHint}>{currentMode.chatHint}</p>

            {/* Divider */}
            <div style={S.sectionDivider} />

            {/* Requested count */}
            <div style={S.countRow}>
              <label htmlFor="photo-count" style={S.countLabel}>I want</label>
              <input
                id="photo-count"
                type="number"
                value={requestedCount}
                onChange={(e) => {
                  const n = parseInt(e.target.value, 10);
                  if (!isNaN(n) && n > 0 && n <= 9999) setRequestedCount(n);
                }}
                min={1}
                max={9999}
                style={S.countInput}
                className="frame-focus"
                inputMode="numeric"
                onFocus={(e) => e.target.select()}
              />
              <span style={S.countLabel}>photos</span>
            </div>
            <p style={S.countHint}>
              FRAME will keep your top {requestedCount} using {currentMode.label} scoring
            </p>

            {/* Score CTA button */}
            <button
              className="frame-cta frame-focus"
              onClick={handleScore}
              disabled={scoring || files.length === 0}
              style={{
                ...S.scoreCTA,
                opacity: scoring ? 0.6 : 1,
                cursor: scoring ? 'not-allowed' : 'pointer',
              }}
              aria-busy={scoring}
            >
              {scoring
                ? `Scoring ${progress.current} of ${progress.total}...`
                : '★  Score My Photos'}
            </button>

            {/* Usage counter / tier status */}
            {!authState.isAuthenticated ? (
              <p style={{ fontSize: 13, color: C.secondary, textAlign: 'center', marginTop: 8 }}>
                {UsageTracker.photosRemaining() > 0 ? (
                  <>{UsageTracker.photosRemaining()} free photos remaining</>
                ) : (
                  <>Free limit reached — <button onClick={() => { setAuthMode('signup'); setShowAuthGate(true); }} style={{ color: C.gold, background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', padding: 0, font: 'inherit' }}>create a free account</button> to continue</>
                )}
              </p>
            ) : userTier === SubscriptionTier.Free ? (
              <p style={{ fontSize: 13, color: C.secondary, textAlign: 'center', marginTop: 8 }}>
                Free tier — {entitlements.photosPerSession[selectedMode] === 0 ? (
                  <>this mode requires <button onClick={() => setShowUpgrade(true)} style={{ color: C.gold, background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', padding: 0, font: 'inherit' }}>Silver or Gold</button></>
                ) : entitlements.photosPerSession[selectedMode] === Infinity ? (
                  <>unlimited in Balanced mode</>
                ) : (
                  <>{Math.max(0, entitlements.photosPerSession[selectedMode] - (sessionPhotosScored[selectedMode] || 0))} of {entitlements.photosPerSession[selectedMode]} photos left in {SCORING_MODES[selectedMode].label} &middot; <button onClick={() => setShowUpgrade(true)} style={{ color: C.gold, background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', padding: 0, font: 'inherit' }}>Upgrade</button></>
                )}
              </p>
            ) : null}

            {/* Progress bar */}
            {scoring && (
              <div style={{ marginTop: 16 }}>
                <div style={S.progressTrack} role="progressbar" aria-valuenow={progress.current} aria-valuemax={progress.total}>
                  <div style={{
                    ...S.progressFill,
                    width: `${progress.total > 0 ? (progress.current / progress.total) * 100 : 0}%`,
                  }} />
                </div>
                <p style={S.privacyNote}>🔒 All scoring happens in your browser</p>
              </div>
            )}
          </section>
        )}

        {/* ─── Results ─── */}
        {results.length > 0 && (
          <section ref={resultsRef} style={S.card} className="frame-animate-in" role="region" aria-label="Scoring results">
            <h3 style={S.sectionLabel}>
              Results — keeping top {selectedCount} of {results.length}
            </h3>

            {/* Score histogram */}
            <div style={S.histogram} role="img" aria-label="Score distribution chart">
              {(() => {
                const bins = new Array(10).fill(0);
                results.forEach((s) => bins[Math.min(9, Math.floor(s.compositeScore))]++);
                const maxBin = Math.max(...bins, 1);
                const threshold = results.length > selectedCount
                  ? results[selectedCount - 1]?.compositeScore ?? 0
                  : 0;
                return bins.map((count, i) => (
                  <div key={i} style={S.histBarWrap}>
                    <div style={{
                      ...S.histBar,
                      height: `${Math.max((count / maxBin) * 100, 3)}%`,
                      backgroundColor: i + 0.5 >= threshold ? scoreColor(i + 0.5) : C.divider,
                    }} />
                    {i % 2 === 0 && <span style={S.histLabel}>{i}</span>}
                  </div>
                ));
              })()}
            </div>

            {/* Photo grid — responsive 3-col mobile, 4-col tablet, 5-col desktop */}
            <div style={S.photoGrid}>
              {results.map((photo, i) => {
                const isSelected = i < selectedCount;
                return (
                  <div
                    key={photo.id}
                    className="frame-photo frame-focus"
                    onClick={() => setSelectedPhoto(photo)}
                    style={{
                      ...S.photoCard,
                      borderColor: isSelected ? C.blue : 'transparent',
                      opacity: isSelected ? 1 : 0.5,
                      cursor: 'pointer',
                    }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && setSelectedPhoto(photo)}
                    aria-label={`Photo score ${formatScore(photo.compositeScore)}`}
                  >
                    {photo.thumbnailUri && (
                      <img src={photo.thumbnailUri} alt="" style={S.photoImg} loading="lazy" />
                    )}
                    <div style={{
                      ...S.scoreBadge,
                      backgroundColor: scoreColor(photo.compositeScore),
                    }}>
                      {formatScore(photo.compositeScore)}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* ─── Score Card Modal ─── */}
        {selectedPhoto && (
          <div
            className="frame-modal-backdrop"
            style={S.modalOverlay}
            onClick={() => setSelectedPhoto(null)}
            role="dialog"
            aria-modal="true"
            aria-label="Photo score card"
          >
            <div style={S.modal} onClick={(e) => e.stopPropagation()} className="frame-scroll">
              <button
                onClick={() => setSelectedPhoto(null)}
                style={S.modalClose}
                className="frame-focus"
                aria-label="Close score card"
              >✕</button>

              {selectedPhoto.thumbnailUri && (
                <img src={selectedPhoto.thumbnailUri} alt="" style={S.modalImg} />
              )}

              <div style={S.modalBody}>
                {/* Composite score */}
                <div style={S.modalScoreRow}>
                  <span style={S.modalComposite}>
                    {formatScore(selectedPhoto.compositeScore)}
                  </span>
                  <span style={S.modalPctLabel}>
                    {selectedPhoto.dimensionNotes?._percentile || percentileLabel(selectedPhoto.compositeScore, allComposites)}
                  </span>
                </div>

                {/* Dimension bars */}
                {DIMENSIONS.map((dim) => {
                  const score =
                    dim.key === 'portraitComposition' ? selectedPhoto.dimensions.portraitComposition :
                    dim.key === 'lighting' ? selectedPhoto.dimensions.lighting :
                    dim.key === 'composition' ? selectedPhoto.dimensions.composition :
                    selectedPhoto.dimensions.sceneInterestSpiked;
                  if (score === null) return null;

                  const barColor = scoreColor(score);
                  return (
                    <div key={dim.key} style={S.dimRow}>
                      <div style={S.dimHeader}>
                        <span style={S.dimLabel}>{dim.label}</span>
                        <span style={{ ...S.dimScore, color: barColor }}>
                          {formatScore(score)}
                        </span>
                      </div>
                      <div style={S.dimTrack}>
                        <div
                          className="frame-bar-animate"
                          style={{
                            ...S.dimFill,
                            width: `${score * 10}%`,
                            backgroundColor: barColor,
                          }}
                        />
                      </div>
                      <p style={S.dimNote}>{dim.noteForScore(score)}</p>
                    </div>
                  );
                })}

                {/* Badges */}
                {selectedPhoto.landmarkTier > 0 && (
                  <div style={S.badge}>
                    🗺 Landmark detected (+{formatScore(
                      selectedPhoto.dimensions.sceneInterestSpiked - selectedPhoto.dimensions.sceneInterest
                    )})
                  </div>
                )}
                {selectedPhoto.detractorPenalty > 0.1 && (
                  <div style={{ ...S.badge, ...S.detractorBadge }}>
                    ⚠ Busy background −{formatScore(selectedPhoto.detractorPenalty)}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ─── App Download CTA ─── */}
        <section style={S.ctaSection}>
          <p style={S.ctaText}>
            Get the full experience — 7 scoring modes, AI editing, and more
          </p>
          <p style={S.ctaSubtext}>Coming soon to iOS and Android</p>
        </section>

        {/* ─── Footer ─── */}
        <footer style={S.footer}>
          <a href="https://sandstonecloud.com" target="_blank" rel="noopener noreferrer" style={{ display: 'inline-block', marginBottom: 10, opacity: 0.7, transition: 'opacity 0.2s' }} onMouseEnter={e => (e.currentTarget.style.opacity = '1')} onMouseLeave={e => (e.currentTarget.style.opacity = '0.7')}>
            <img src="/sandstone-logo.png" alt="Sandstone Cloud" style={{ height: 40, width: 'auto' }} />
          </a>
          <br />
          FRAME — Photo Companion by Sandstone Cloud
          <br />Oliver La Roche &amp; Jemell Sanders
        </footer>
      </main>

      {/* ─── Auth Gate Modal ─── */}
      {showAuthGate && (
        <div style={S.modalOverlay} onClick={() => setShowAuthGate(false)}>
          <div style={{ ...S.modalCard, maxWidth: 400 }} onClick={e => e.stopPropagation()}>
            <h3 style={{ color: C.text, fontSize: 20, marginBottom: 4 }}>
              {authMode === 'signup' ? 'Create Your Account' : authMode === 'login' ? 'Welcome Back' : authMode === 'verify' ? 'Check Your Email' : 'Reset Password'}
            </h3>
            <p style={{ color: C.secondary, fontSize: 14, marginBottom: 20 }}>
              {authMode === 'signup' ? 'Create a free account to keep scoring photos' : authMode === 'verify' ? `We sent a 6-digit code to ${authEmail}` : 'Enter your email to continue'}
            </p>

            {authError && <p style={{ color: '#ef4444', fontSize: 13, marginBottom: 12 }}>{authError}</p>}

            {(authMode === 'signup' || authMode === 'login') && (
              <>
                <input type="email" placeholder="Email" value={authEmail} onChange={e => setAuthEmail(e.target.value)}
                  style={{ ...S.countInput, width: '100%', marginBottom: 12, padding: '10px 12px', fontSize: 15 }} />
                <input type="password" placeholder="Password (8+ characters)" value={authPassword} onChange={e => setAuthPassword(e.target.value)}
                  style={{ ...S.countInput, width: '100%', marginBottom: 16, padding: '10px 12px', fontSize: 15 }} />
                <button
                  disabled={authLoading || !authEmail || !authPassword}
                  onClick={async () => {
                    setAuthLoading(true); setAuthError('');
                    try {
                      // Admin override — local auth, no Cognito
                      if (authMode === 'login' && authEmail === ADMIN_USERNAME && authPassword === ADMIN_PASSWORD) {
                        setAuthState({ isAuthenticated: true, user: { userId: 'admin-0', email: 'admin@sandstonecloud.com' }, isLoading: false });
                        setUserTier(SubscriptionTier.Gold);
                        setIsAdmin(true);
                        setShowAuthGate(false);
                        setAuthLoading(false);
                        return;
                      }

                      if (authMode === 'signup') {
                        await WebAuthService.signUp(authEmail, authPassword);
                        setAuthMode('verify');
                      } else {
                        await WebAuthService.signIn(authEmail, authPassword);
                        const user = await WebAuthService.getCurrentUser();
                        if (user) setAuthState({ isAuthenticated: true, user: { userId: user.userId, email: authEmail }, isLoading: false });
                        setShowAuthGate(false);
                      }
                    } catch (err: any) {
                      if (err?.name === 'UserNotConfirmedException') setAuthMode('verify');
                      else setAuthError(err?.message || 'Something went wrong');
                    } finally { setAuthLoading(false); }
                  }}
                  style={{ ...S.scoreCTA, width: '100%', opacity: authLoading ? 0.6 : 1 }}
                >
                  {authLoading ? 'Please wait...' : authMode === 'signup' ? 'Create Account' : 'Log In'}
                </button>
                <p style={{ color: C.secondary, fontSize: 13, textAlign: 'center', marginTop: 12 }}>
                  {authMode === 'signup' ? (
                    <>Already have an account? <button onClick={() => { setAuthMode('login'); setAuthError(''); }} style={{ color: C.gold, background: 'none', border: 'none', cursor: 'pointer', font: 'inherit' }}>Log in</button></>
                  ) : (
                    <>Need an account? <button onClick={() => { setAuthMode('signup'); setAuthError(''); }} style={{ color: C.gold, background: 'none', border: 'none', cursor: 'pointer', font: 'inherit' }}>Sign up</button></>
                  )}
                </p>
              </>
            )}

            {authMode === 'verify' && (
              <>
                <input type="text" placeholder="6-digit code" value={authCode} onChange={e => setAuthCode(e.target.value)} maxLength={6}
                  style={{ ...S.countInput, width: '100%', marginBottom: 16, padding: '10px 12px', fontSize: 18, textAlign: 'center', letterSpacing: 8 }} inputMode="numeric" />
                <button
                  disabled={authLoading || authCode.length < 6}
                  onClick={async () => {
                    setAuthLoading(true); setAuthError('');
                    try {
                      await WebAuthService.confirmSignUp(authEmail, authCode);
                      await WebAuthService.signIn(authEmail, authPassword);
                      const user = await WebAuthService.getCurrentUser();
                      if (user) setAuthState({ isAuthenticated: true, user: { userId: user.userId, email: authEmail }, isLoading: false });
                      setShowAuthGate(false);
                    } catch (err: any) {
                      setAuthError(err?.message || 'Verification failed');
                    } finally { setAuthLoading(false); }
                  }}
                  style={{ ...S.scoreCTA, width: '100%', opacity: authLoading ? 0.6 : 1 }}
                >
                  {authLoading ? 'Verifying...' : 'Verify & Continue'}
                </button>
              </>
            )}

            <button onClick={() => setShowAuthGate(false)} style={{ color: C.muted, background: 'none', border: 'none', cursor: 'pointer', marginTop: 16, fontSize: 13, display: 'block', width: '100%', textAlign: 'center' }}>
              Continue as guest
            </button>
          </div>
        </div>
      )}

      {/* ─── Upgrade Modal ─── */}
      {showUpgrade && (
        <div style={S.modalOverlay} onClick={() => setShowUpgrade(false)}>
          <div style={{ ...S.modalCard, maxWidth: 440 }} onClick={e => e.stopPropagation()}>
            <h3 style={{ color: C.text, fontSize: 20, marginBottom: 16 }}>Upgrade FRAME</h3>

            <div style={{ background: C.surface, borderRadius: 12, padding: 16, marginBottom: 12, border: `1px solid ${C.divider}` }}>
              <h4 style={{ color: C.gold, fontSize: 16, marginBottom: 4 }}>Silver — $4.99/mo</h4>
              <p style={{ color: C.secondary, fontSize: 13, lineHeight: 1.5 }}>250 photos/month &middot; All 7 modes &middot; Full vibe engine &middot; 20 clean downloads</p>
              <p style={{ color: C.muted, fontSize: 12, marginTop: 4 }}>Annual: $39.99/year (save 33%)</p>
            </div>

            <div style={{ background: C.surface, borderRadius: 12, padding: 16, marginBottom: 16, border: `1px solid ${C.gold}` }}>
              <h4 style={{ color: C.gold, fontSize: 16, marginBottom: 4 }}>Gold — $9.99/mo</h4>
              <p style={{ color: C.secondary, fontSize: 13, lineHeight: 1.5 }}>Unlimited photos &middot; AI editing &middot; Compositing &middot; Social calibration</p>
              <p style={{ color: C.muted, fontSize: 12, marginTop: 4 }}>Annual: $79.99/year (save 33%)</p>
            </div>

            <p style={{ color: C.muted, fontSize: 12, textAlign: 'center' }}>Subscriptions managed through the FRAME iOS app</p>
            <button onClick={() => setShowUpgrade(false)} style={{ color: C.secondary, background: 'none', border: 'none', cursor: 'pointer', marginTop: 12, fontSize: 14, display: 'block', width: '100%', textAlign: 'center' }}>
              Maybe later
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Styles ─────────────────────────────────────────────────
// All responsive via clamp/min/max — no media queries needed.
// Safe areas handled via env() for iOS notch/dynamic island.
// Touch targets ≥ 44px on all interactive elements.
const S: Record<string, React.CSSProperties> = {
  page: {
    minHeight: '100vh',
    backgroundColor: C.bg,
  },

  // ── Header ──
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: 'max(16px, env(safe-area-inset-top, 16px)) clamp(16px, 4vw, 32px) 12px',
    position: 'sticky', top: 0, zIndex: 50,
    backgroundColor: 'rgba(15,23,42,0.9)',
    borderBottom: `1px solid ${C.divider}`,
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
  },
  brand: {
    fontSize: 'clamp(22px, 4vw, 28px)', fontWeight: 800, color: C.text,
    letterSpacing: 3, lineHeight: 1, margin: 0,
    fontFamily: "'Inter', system-ui, sans-serif",
  },
  brandSub: { fontSize: 12, color: C.secondary, margin: '2px 0 0' },
  privacyLink: {
    fontSize: 14, color: C.gold, textDecoration: 'none',
    padding: '12px 0', minWidth: 44, textAlign: 'right' as const,
  },
  headerLink: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 15, fontWeight: 500, color: C.gold, textDecoration: 'none',
    padding: '8px 8px', minHeight: 44, lineHeight: 1,
    cursor: 'pointer', border: 'none', background: 'none',
    fontFamily: "'Inter', system-ui, sans-serif",
    verticalAlign: 'middle',
  },

  // ── Main container ──
  main: {
    maxWidth: 680, margin: '0 auto',
    padding: '0 clamp(12px, 4vw, 24px) max(32px, env(safe-area-inset-bottom, 32px))',
  },

  // ── Hero ──
  hero: { textAlign: 'center' as const, padding: 'clamp(28px, 6vw, 56px) 0 20px' },
  heroTitle: {
    fontSize: 'clamp(24px, 5vw, 40px)', fontWeight: 700, color: C.text,
    lineHeight: 1.2, margin: '0 0 14px',
    fontFamily: "'Inter', system-ui, sans-serif",
  },
  heroBody: {
    fontSize: 'clamp(15px, 2.5vw, 18px)', color: C.secondary,
    lineHeight: 1.6, maxWidth: 520, margin: '0 auto',
  },

  // ── Cards ──
  card: {
    backgroundColor: C.surface, borderRadius: 20,
    padding: 'clamp(16px, 3vw, 24px)',
    marginBottom: 16,
    boxShadow: '0 2px 12px rgba(0,0,0,0.2)',
    border: `1px solid ${C.divider}`,
  },
  sectionLabel: {
    fontSize: 15, fontWeight: 600, color: C.text, margin: '0 0 12px',
    letterSpacing: 0.3,
  },
  sectionDivider: {
    height: 1, backgroundColor: C.divider, margin: '20px 0',
  },

  // ── Dropzone ──
  dropzone: {
    border: `2px dashed ${C.surfaceHover}`, borderRadius: 16,
    padding: 'clamp(20px, 4vw, 44px) 16px', textAlign: 'center' as const,
    cursor: 'pointer', backgroundColor: 'rgba(30,41,59,0.6)',
    transition: 'border-color 0.2s, background-color 0.2s',
  },
  uploadIcon: { fontSize: 36, marginBottom: 12 },
  dropzoneTitle: { fontSize: 17, fontWeight: 600, color: C.text, margin: '0 0 6px' },
  dropzoneCount: { fontSize: 17, fontWeight: 600, color: C.gold, margin: '0 0 4px' },
  dropzoneHint: { fontSize: 14, color: C.secondary, margin: 0, lineHeight: 1.5 },
  thumbStrip: {
    display: 'flex', gap: 6, justifyContent: 'center',
    marginTop: 14, flexWrap: 'wrap' as const,
  },
  thumbImg: {
    width: 'clamp(40px, 10vw, 52px)', height: 'clamp(40px, 10vw, 52px)',
    borderRadius: 8, objectFit: 'cover' as const,
  },
  thumbMore: {
    width: 'clamp(40px, 10vw, 52px)', height: 'clamp(40px, 10vw, 52px)',
    borderRadius: 8, backgroundColor: C.divider,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 13, fontWeight: 600, color: C.secondary,
  },

  // ── Mode chips (horizontal scrollable on small screens) ──
  modeChipScroll: {
    overflowX: 'auto' as const, marginLeft: -4, marginRight: -4,
    paddingBottom: 4, WebkitOverflowScrolling: 'touch',
  },
  modeChipRow: {
    display: 'flex', gap: 8, paddingLeft: 4, paddingRight: 4,
    width: 'max-content',
  },
  modeChip: {
    padding: '10px 18px', borderRadius: 999,
    border: 'none', display: 'inline-flex', alignItems: 'center', gap: 5,
    fontSize: 14, transition: 'all 0.15s',
    minHeight: 44, whiteSpace: 'nowrap' as const,
    WebkitTapHighlightColor: 'transparent',
  },
  lockBadge: { fontSize: 11, lineHeight: 1, opacity: 0.7 },
  tagline: {
    fontStyle: 'italic', fontSize: 13, color: C.secondary,
    margin: '8px 0 0', lineHeight: 1.4,
  },

  // ── Weight bars ──
  weightGrid: {
    display: 'flex', flexDirection: 'column' as const, gap: 12,
  },
  weightRow: {
    display: 'flex', alignItems: 'center', gap: 10,
  },
  weightLabel: {
    fontSize: 13, color: C.text, flex: '1 1 auto', minWidth: 0,
    whiteSpace: 'nowrap' as const, overflow: 'hidden', textOverflow: 'ellipsis',
    lineHeight: 1.3,
  },
  weightBarTrack: {
    width: 'clamp(60px, 15vw, 100px)', height: 6, backgroundColor: C.divider,
    borderRadius: 3, flexShrink: 0, overflow: 'hidden',
  },
  weightBarFill: {
    height: '100%', backgroundColor: C.blue, borderRadius: 3,
    transition: 'width 0.4s cubic-bezier(0.22, 1, 0.36, 1)',
  },
  weightPct: {
    fontSize: 12, fontWeight: 600, color: C.gold, width: 36,
    textAlign: 'right' as const, flexShrink: 0,
  },

  // ── Vibe input (ENLARGED chat box) ──
  vibeInput: {
    width: '100%',
    minHeight: 220,
    maxHeight: 400,
    padding: 16,
    fontSize: 16,
    lineHeight: '24px',
    color: C.text,
    backgroundColor: 'rgba(15,23,42,0.6)',
    border: `1.5px solid ${C.divider}`,
    borderRadius: 16,
    resize: 'vertical' as const,
    outline: 'none',
    fontFamily: 'inherit',
    WebkitAppearance: 'none' as const,
    display: 'block',
    boxSizing: 'border-box' as const,
  },
  chatHint: {
    fontSize: 12, color: C.secondary, margin: '8px 0 0',
    fontStyle: 'italic', lineHeight: 1.4,
  },

  // ── Count selector ──
  countRow: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    gap: 8, marginBottom: 6,
  },
  countLabel: { fontSize: 16, color: C.text },
  countInput: {
    width: 64, fontSize: 18, fontWeight: 700, color: C.gold,
    textAlign: 'center' as const, border: 'none',
    borderBottom: `2px solid ${C.gold}`, backgroundColor: 'transparent',
    padding: '4px 0', outline: 'none',
    WebkitAppearance: 'none' as const, MozAppearance: 'textfield' as const,
    minHeight: 44,
  },
  countHint: {
    fontSize: 13, color: C.secondary, textAlign: 'center' as const,
    margin: '0 0 20px', lineHeight: 1.4,
  },

  // ── Score CTA ──
  scoreCTA: {
    width: '100%', padding: '16px 24px', fontSize: 18, fontWeight: 600,
    color: C.white, backgroundColor: C.blue, border: 'none',
    borderRadius: 999, transition: 'all 0.2s',
    minHeight: 56, letterSpacing: 0.3,
    WebkitTapHighlightColor: 'transparent',
  },

  // ── Progress ──
  progressTrack: {
    width: '100%', height: 6, backgroundColor: C.divider,
    borderRadius: 3, overflow: 'hidden',
  },
  progressFill: {
    height: '100%', backgroundColor: C.blue, borderRadius: 3,
    transition: 'width 0.3s ease',
  },
  privacyNote: {
    fontSize: 13, color: C.secondary, textAlign: 'center' as const,
    margin: '10px 0 0',
  },

  // ── Histogram ──
  histogram: {
    display: 'flex', alignItems: 'flex-end', gap: 4,
    height: 80, marginBottom: 16, padding: '0 4px',
  },
  histBarWrap: {
    flex: 1, display: 'flex', flexDirection: 'column' as const,
    alignItems: 'center', height: '100%', justifyContent: 'flex-end',
  },
  histBar: {
    width: '100%', borderRadius: 2, minHeight: 3,
    transition: 'height 0.4s cubic-bezier(0.22, 1, 0.36, 1)',
  },
  histLabel: { fontSize: 10, color: C.secondary, marginTop: 4 },

  // ── Photo grid ──
  photoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(clamp(80px, 20vw, 120px), 1fr))',
    gap: 'clamp(6px, 1.5vw, 10px)',
  },
  photoCard: {
    position: 'relative' as const, borderRadius: 12, overflow: 'hidden',
    aspectRatio: '1', border: '2.5px solid transparent',
    transition: 'border-color 0.2s, opacity 0.2s, transform 0.15s',
    backgroundColor: C.bg,
  },
  photoImg: {
    width: '100%', height: '100%', objectFit: 'cover' as const,
    display: 'block',
  },
  scoreBadge: {
    position: 'absolute' as const, bottom: 6, right: 6,
    padding: '3px 8px', borderRadius: 8,
    fontSize: 12, fontWeight: 700, color: C.white,
    backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)',
    lineHeight: 1.3,
  },

  // ── Modal ──
  modalOverlay: {
    position: 'fixed' as const, inset: 0,
    backgroundColor: 'rgba(0,0,0,0.55)',
    zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: 'clamp(8px, 2vw, 24px)',
    backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
  },
  modal: {
    backgroundColor: C.surface, borderRadius: 24, overflow: 'hidden',
    width: '100%', maxWidth: 420,
    maxHeight: '90dvh',
    overflowY: 'auto' as const,
    boxShadow: '0 24px 48px rgba(0,0,0,0.5)',
    position: 'relative' as const,
    WebkitOverflowScrolling: 'touch',
    border: `1px solid ${C.divider}`,
  },
  modalClose: {
    position: 'absolute' as const, top: 12, right: 12, zIndex: 10,
    width: 40, height: 40, borderRadius: 20,
    backgroundColor: 'rgba(0,0,0,0.45)', color: '#fff',
    fontSize: 17, fontWeight: 600, display: 'flex',
    alignItems: 'center', justifyContent: 'center',
    border: 'none', cursor: 'pointer',
    WebkitTapHighlightColor: 'transparent',
  },
  modalImg: {
    width: '100%', height: 'clamp(180px, 35vw, 260px)',
    objectFit: 'cover' as const, display: 'block',
  },
  modalBody: { padding: 'clamp(16px, 3vw, 24px)' },
  modalScoreRow: { textAlign: 'center' as const, marginBottom: 20 },
  modalComposite: { fontSize: 48, fontWeight: 800, color: C.gold, display: 'block' },
  modalPctLabel: { display: 'block', fontSize: 14, color: C.secondary, marginTop: 4 },

  // ── Dimension bars (in modal) ──
  dimRow: { marginBottom: 16 },
  dimHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    marginBottom: 5,
  },
  dimLabel: { fontSize: 14, fontWeight: 500, color: C.text },
  dimScore: { fontSize: 16, fontWeight: 700 },
  dimTrack: {
    height: 6, backgroundColor: C.divider, borderRadius: 3, overflow: 'hidden',
  },
  dimFill: {
    height: '100%', borderRadius: 3,
  },
  dimNote: {
    fontSize: 12, color: C.secondary, fontStyle: 'italic', margin: '4px 0 0',
    lineHeight: 1.3,
  },

  // ── Badges ──
  badge: {
    display: 'inline-block', backgroundColor: 'rgba(212,165,116,0.15)', borderRadius: 8,
    padding: '6px 12px', fontSize: 13, fontWeight: 500, color: C.gold,
    marginTop: 8, marginRight: 6, lineHeight: 1.3,
  },
  detractorBadge: { backgroundColor: 'rgba(239,68,68,0.08)', color: C.error },

  // ── CTA section ──
  ctaSection: { textAlign: 'center' as const, padding: '36px 0 16px' },
  ctaText: { fontSize: 16, fontWeight: 600, color: C.text, margin: '0 0 4px' },
  ctaSubtext: { fontSize: 14, color: C.secondary, margin: 0 },

  // ── Footer ──
  footer: {
    textAlign: 'center' as const,
    padding: '24px 0 max(40px, env(safe-area-inset-bottom, 40px))',
    fontSize: 13, color: C.secondary, lineHeight: 1.6,
  },
};
